/**
 * BlogCategoryPage - Blog posts filtered by category
 */

import { useParams, Navigate, Link } from "react-router-dom";
import { usePageTitle } from "../../hooks";
import { getPostsByCategory, getCategoryBySlug, CATEGORIES } from "../../lib/blog";
import { SEOHead, BlogCard } from "../../components/blog";
import type { CategorySlug } from "../../lib/blog";

export function BlogCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const categoryInfo = getCategoryBySlug(category || "");

  // Redirect to 404 if category not found
  if (!categoryInfo) {
    return <Navigate to="/404" replace />;
  }

  usePageTitle(`${categoryInfo.name} - Blog`);
  const posts = getPostsByCategory(category as CategorySlug);

  return (
    <div className="flex flex-col">
      <SEOHead
        title={`${categoryInfo.name} Articles`}
        description={categoryInfo.description}
        canonical={`/blog/${category}`}
        type="website"
      />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-magic-100 via-sunshine-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-body text-body-sm text-neutral-500 hover:text-magic-600 mb-4"
          >
            <span>←</span>
            All posts
          </Link>
          <h1 className="font-display font-extrabold text-display-md md:text-display-lg text-neutral-900 mb-4">
            {categoryInfo.name}
          </h1>
          <p className="font-body text-body-lg text-neutral-600 max-w-xl mx-auto">
            {categoryInfo.description}
          </p>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-6 bg-white border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.slug}
                to={`/blog/${cat.slug}`}
                className={`px-4 py-2 rounded-full font-body text-body-sm font-semibold
                         transition-colors
                         ${
                           cat.slug === category
                             ? "bg-magic-500 text-white"
                             : "bg-neutral-100 text-neutral-700 hover:bg-magic-100 hover:text-magic-700"
                         }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          {posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={`${post.category}/${post.slug}`} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="font-body text-body-lg text-neutral-500">
                No posts in this category yet. Check back soon!
              </p>
              <Link
                to="/blog"
                className="inline-block mt-4 font-body text-body-md text-magic-600 hover:text-magic-700"
              >
                View all posts →
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default BlogCategoryPage;
